/**
 * RegistryUtils.js
 * Helpers for the VoiceRegistry (Global frequency/time awareness).
 * Ensures instruments listen to each other to avoid frequency masking and muddy mixes.
 */

/**
 * Initializes a new empty registry with methods.
 */
export const createRegistry = () => ({
    occupied: [], // List of { s, e, minPitch, maxPitch, role, notes }

    // Method to check busy range
    isRangeBusy(rangeName, time) {
        let min = 0, max = 127;
        if (rangeName === 'low') { min = 0; max = 48; } // < C3
        else if (rangeName === 'mid') { min = 48; max = 72; } // C3 - C5
        else if (rangeName === 'high') { min = 72; max = 127; } // > C5

        let count = 0;
        for (const entry of this.occupied) {
            // Check time overlap
            if (time >= entry.s && time < entry.e) {
                // Check range overlap
                // entry range [eMin, eMax] vs target [min, max]
                if (entry.maxPitch >= min && entry.minPitch <= max) {
                    count++;
                }
            }
        }
        return count >= 2; // Busy if 2+ voices
    },

    // Method to check harmonic buffer for the whole bar
    // Used by "Elastic Anchor" to find sustained clashes
    getBarHarmonicBuffer(barIndex) {
        // Filter occupied list for entries crossing this bar
        const barStart = barIndex * 4;
        const barEnd = barStart + 4;

        return this.occupied.filter(entry => {
            // Check geometric overlap: start < end && end > start
            return entry.s < barEnd && entry.e > barStart;
        }).flatMap(entry => entry.notes);
    }
});

/**
 * Adds generated events to the registry.
 * @param {Object} registry - The voice registry object.
 * @param {Array} events - List of generated MIDI events.
 */
export const addToRegistry = (registry, events) => {
    if (!registry || !events || !Array.isArray(events)) return;

    events.forEach(ev => {
        // Calculate duration/end time
        const start = ev.s;
        const len = ev.len || 0.1;
        const end = start + len;

        // Determine Pitch Range
        let minPitch = 0;
        let maxPitch = 127;
        let notes = [];

        if (Array.isArray(ev.notes) && ev.notes.length > 0) {
            notes = ev.notes;
            minPitch = Math.min(...notes);
            maxPitch = Math.max(...notes);
        } else if (typeof ev.d === 'number') {
            notes = [ev.d];
            minPitch = ev.d;
            maxPitch = ev.d;
        } else {
            return; // No pitch data
        }

        registry.occupied.push({
            s: start,
            e: end,
            minPitch,
            maxPitch,
            notes,
            role: ev.role || 'unknown'
        });
    });
};

/**
 * Checks if a specific pitch at a specific time clashes with existing voices.
 * @param {Object} registry - The voice registry.
 * @param {number} time - Time in beats (absolute).
 * @param {number} pitch - MIDI pitch to check.
 * @param {string} excludeRole - Role to ignore (e.g. self-checking).
 * @returns {number} Clash penalty score (0 = clean, >0 = penalty).
 */
export const checkClash = (registry, time, pitch, excludeRole = null) => {
    if (!registry || !registry.occupied) return 0;

    let penalty = 0;

    for (const entry of registry.occupied) {
        if (excludeRole && entry.role === excludeRole) continue;

        // 1. Time Overlap Check
        // Allow slight grace period to avoid penalties on consecutive notes
        // Check if time is strictly inside [s, e)
        if (time >= entry.s && time < entry.e) {
            // 2. Pitch Clash Check
            for (const occupiedPitch of entry.notes) {
                const diff = Math.abs(occupiedPitch - pitch);

                // Exact Unison (0)
                // If distinct roles (e.g. Bass vs Melody), Unison is okay (doubling).
                // But Harmony vs Melody Unison can be masking.
                // We'll treat Unison as 0 penalty (allowed doubling) for now unless specific constraints.
                if (diff === 0) {
                    penalty += 50; // Treat Unison as Clash for now (Masking)
                }
                // Minor 2nd (1 semitone) -> BAD CLASH
                else if (diff === 1) {
                    penalty += 50; // Heavy penalty
                }
                // Major 2nd (2 semitones) -> Mild friction (Cluster)
                else if (diff === 2) {
                    // Allowed in clusters, but maybe penalize slightly if clean mix desired
                    penalty += 10;
                }
            }
        }
    }

    return penalty;
};

/**
 * Checks if a frequency range is crowded.
 * Useful for spectrum management (e.g. "Is the low end busy?").
 */
export const isRangeBusy = (registry, time, minPitch, maxPitch) => {
    if (!registry || !registry.occupied) return false;

    return registry.occupied.some(entry => {
        if (time >= entry.s && time < entry.e) {
            // Check overlap
            return (entry.maxPitch >= minPitch && entry.minPitch <= maxPitch);
        }
        return false;
    });
};
